import { useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { articles } from '@/mocks/data';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { WebPageSchema, BreadcrumbListSchema, ArticleSchema } from '@/components/feature/SEOMeta';

const articleImages: Record<string, string> = {
  'season-1-cast-reveal': 'https://readdy.ai/api/search-image?query=Group%20of%20diverse%20attractive%20young%20Asian%20people%20posing%20on%20tropical%20beach%2C%20reality%20TV%20cast%20photo%2C%20cinematic%20lighting%2C%20warm%20vibrant%20colors%2C%20professional%20photography&width=1200&height=675&seq=art-detail-1&orientation=landscape',
  'island-location-guide': 'https://readdy.ai/api/search-image?query=Luxury%20private%20tropical%20island%20with%20beach%20villas%2C%20crystal%20clear%20turquoise%20water%2C%20palm%20trees%2C%20aerial%20view%2C%20cinematic%20lighting%2C%20paradise&width=1200&height=675&seq=art-detail-2&orientation=landscape',
  'reality-dating-psychology': 'https://readdy.ai/api/search-image?query=Young%20Asian%20couple%20having%20deep%20conversation%20on%20beach%20at%20sunset%2C%20documentary%20style%2C%20cinematic%20lighting%2C%20emotional&width=1200&height=675&seq=art-detail-3&orientation=landscape',
  'how-to-apply': 'https://readdy.ai/api/search-image?query=Young%20Asian%20person%20filling%20out%20application%20form%20with%20tropical%20beach%20background%2C%20hopeful%20expression%2C%20cinematic&width=1200&height=675&seq=art-detail-4&orientation=landscape',
  'asian-dating-culture': 'https://readdy.ai/api/search-image?query=Diverse%20group%20of%20young%20Asian%20couples%20from%20different%20countries%2C%20cultural%20diversity%2C%20tropical%20setting%2C%20cinematic&width=1200&height=675&seq=art-detail-5&orientation=landscape',
  'producer-interview': 'https://readdy.ai/api/search-image?query=TV%20producer%20being%20interviewed%20with%20tropical%20island%20backdrop%2C%20professional%20setting%2C%20cinematic%20lighting&width=1200&height=675&seq=art-detail-6&orientation=landscape',
};

const defaultImg = 'https://readdy.ai/api/search-image?query=Asian%20reality%20TV%20show%20production%20scene%2C%20tropical%20island%20lifestyle%2C%20young%20diverse%20Asian%20people%2C%20cinematic&width=1200&height=675&seq=art-detail-default&orientation=landscape';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(() => {
    return articles.find((a) => a.slug === slug);
  }, [slug]);

  const related = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.id !== article.id && a.category === article.category)
      .slice(0, 3);
  }, [article]);

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - 爱情岛亚洲`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute('content', article.excerpt);
    }
  }, [article]);

  if (!article) {
    return (
      <div className="pt-20 md:pt-24 min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
            <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
          </div>
          <h2 className="text-xl font-heading font-semibold text-foreground-950 mb-2">文章未找到</h2>
          <p className="text-foreground-500 text-sm mb-6">抱歉，您要查看的文章不存在或已被移除。</p>
          <Link to="/category/all" className="inline-flex items-center px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors duration-200">
            返回资讯中心
          </Link>
        </div>
      </div>
    );
  }

  const categorySlugMap: Record<string, string> = {
    '节目资讯': 'program-news',
    '幕后花絮': 'behind-scenes',
    '深度观察': 'deep-dive',
    '报名指南': 'apply-guide',
    '文化专栏': 'culture',
  };
  const categorySlug = categorySlugMap[article.category] || 'all';

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: '资讯中心', to: '/category/all' },
    { label: article.category, to: `/category/${categorySlug}` },
    { label: article.title },
  ];

  const breadcrumbSchemaItems = [
    { name: '首页', item: 'https://www.aiqingdao.asia/' },
    { name: '资讯中心', item: 'https://www.aiqingdao.asia/category/all' },
    { name: article.category, item: `https://www.aiqingdao.asia/category/${categorySlug}` },
    { name: article.title },
  ];

  return (
    <>
      <WebPageSchema
        title={`${article.title} - 爱情岛亚洲`}
        description={article.excerpt}
        url={`https://www.aiqingdao.asia/article/${article.slug}`}
      />
      <BreadcrumbListSchema items={breadcrumbSchemaItems} />
      <ArticleSchema
        headline={article.title}
        description={article.excerpt}
        url={`https://www.aiqingdao.asia/article/${article.slug}`}
        datePublished={article.publishDate}
        author={article.author}
        image={articleImages[article.slug] || defaultImg}
      />

      <article className="pt-20 md:pt-24 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          <div className="py-6 md:py-10">
            <div className="mb-6">
              <span className="inline-block text-xs px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 font-medium mb-4">
                {article.category}
              </span>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-500">
                <span><i className="ri-user-line mr-1"></i>{article.author}</span>
                <span><i className="ri-calendar-line mr-1"></i>{article.publishDate}</span>
              </div>
            </div>

            <div className="aspect-[16/9] rounded-lg overflow-hidden mb-10">
              <img
                src={articleImages[article.slug] || defaultImg}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="prose-custom text-foreground-800 leading-relaxed text-sm md:text-base">
              <p className="mb-4">{article.content}</p>
              <p className="mb-4">
                爱情岛亚洲致力于为每一位嘉宾创造最真实的恋爱体验。在这里，没有剧本，没有彩排，只有最真诚的心与心的碰撞。
                从初见的羞涩，到深入的了解，再到心动的瞬间——每一个画面都被真实记录，每一个情感变化都被镜头捕捉。
              </p>
              <p className="mb-4">
                节目播出以来，受到了亚洲各国观众的广泛关注和热烈讨论。社交媒体上关于节目的讨论热度持续攀升，
                观众们纷纷为自己喜欢的嘉宾打call，也对节目中展现的不同恋爱文化展开深入探讨。
              </p>
              <p>
                爱情岛亚洲不仅仅是一档恋爱真人秀，更是一扇让世界了解亚洲年轻人真实情感世界的窗口。
                我们期待在未来的节目中，看到更多动人的爱情故事在这座美丽的岛屿上绽放。
              </p>
            </div>

            <div className="mt-8 pt-6 border-t border-background-200/70">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-foreground-600 font-medium">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-3 py-1 rounded-full bg-background-100 text-foreground-600 border border-background-200/70"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-8 flex items-center justify-between pt-6 border-t border-background-200/70">
              <Link
                to={`/category/${categorySlug}`}
                className="inline-flex items-center text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors duration-200"
              >
                <i className="ri-arrow-left-line mr-1"></i> 返回{article.category}
              </Link>
              <div className="flex items-center gap-3">
                <span className="text-sm text-foreground-500">分享：</span>
                <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-500 transition-colors duration-200 cursor-pointer" aria-label="分享到微博">
                  <i className="ri-weibo-line"></i>
                </button>
                <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-500 transition-colors duration-200 cursor-pointer" aria-label="复制链接">
                  <i className="ri-link"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </article>

      {related.length > 0 && (
        <section className="py-16 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8">
              相关文章
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((rel) => (
                <Link
                  key={rel.id}
                  to={`/article/${rel.slug}`}
                  className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={articleImages[rel.slug] || defaultImg}
                      alt={rel.title}
                      title={rel.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <span className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 font-medium">
                      {rel.category}
                    </span>
                    <h3 className="text-sm font-heading font-semibold text-foreground-950 mt-2 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2">
                      {rel.title}
                    </h3>
                    <p className="text-xs text-foreground-400 mt-2">{rel.publishDate}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}